import { useCallback, useRef, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { fetchSensors, configureSensors, startWork, fetchStatus, checkDaemonCompatibility, store } from './store/store'
import { isNoData, Modes } from './store/sensors'
import { Button, Checkbox, Radio, Space, Tooltip } from 'antd'
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons'
import { ErrorMessage } from './ErrorMessage'
import { isTrainingBlocked } from './store/daemonCompatibility'
import { DaemonBlockBanner } from './DaemonBlockUi'

function ConnectingState(props) {
  return (
    <div className = "connecting-state">
      <LoadingOutlined className = "connecting-state-icon"/>
      <span className = "connecting-state-message">{ props.message }</span>
    </div>
  )
}

function NoDataState() {
  return (
    <div className = "no-data-state">
      Датчики не найдены
    </div>
  )
}

const sensorTypes = new Map([
  [0x78, 'Датчик пульса'],
  [0x0B, 'Датчик мощности'],
  [0x7B, 'Датчик скорости'],
  [0x7A, 'Датчик каденса'],
  [0x79, 'Датчик скорости и каденса'],
  [0x11, 'Фитнес-тренажер']
])

function getSensorCaption(type) {
  let res = sensorTypes.get(type)
  if (res === undefined) res = `Неизвестный тип датчика ${type}`

  return res
}

const captions = new Map([
  ['cad', 'Каденс'],
  ['spd', 'Скорость'],
  ['pwr', 'Мощность'],
  ['hr', 'Пульс']
])

function getMetricCaption(name) {
  let res = captions.get(name)
  if (res === undefined) res = 'Неизвестно'

  return res
}

function MetricLine(props) {
  const { caption, metric } = props

  return <span className = "sensor-value">{ getMetricCaption(caption) }: { metric.value }</span>
}

const dataSensorId = 'data-sensor-id'
const dataSensorType = 'data-sensor-type'
const dataMetricName = 'data-metric-name'

function MetricsGroup(props) {
  function itemId(item) {
    return item && `${data.name}-${item.id}-${item.type}`
  }

  function dataAttrs(item) {
    return (item && {
      [dataSensorId]: item.id,
      [dataSensorType]: item.type,
      [dataMetricName]: data.name
    }) || {}
  }

  const { data, configured, onSelectChange } = props
  const { items } = data

  if (!items) return

  const [ firstItem ] = items

  if (!firstItem) return

  const caption = (metric) => <span className = { configured ? 'sensor-caption' : 'sensor-name' }>{ getSensorCaption(metric.type) }</span>

  return (
    <div className = "sensor-item">
      {
        configured ?
        <>
          { caption(firstItem) }
          <br/>
          <MetricLine caption = { data.name } metric = { firstItem } />
        </> :
        items.length === 1 ?
        <>
          <Checkbox onChange = { onSelectChange } { ...dataAttrs(firstItem) }>{ caption(firstItem) }</Checkbox>
          <br/>
          <MetricLine caption = { data.name } metric = { firstItem } />
        </> :
        <Radio.Group>
          <Space direction="vertical">
          {
            items.map(item =>
              <div key = { itemId(item) }>
                <Radio value = { itemId(item) } onChange = { onSelectChange } { ...dataAttrs(item) }>{ caption(item) }</Radio>
                <MetricLine caption = { data.name } metric = { item } />
              </div>
            )
          }
          </Space>
        </Radio.Group>
      }
    </div>
  )
}

function isConfigured(mode) {
  return mode === Modes.configured
}

const equipmentMetricNames = new Map([
  ['pwr', 'Мощность'],
  ['cad', 'Каденс'],
  ['spd', 'Скорость'],
  ['hr', 'Пульс']
])

function EquipmentItem(props) {
  const { equip, configured, onSelectChange } = props
  const { id, type, data } = equip

  const metricNames = Object.getOwnPropertyNames(data || {}).filter(name => data[name] !== undefined)
  const attrs = {
    [dataSensorId]: id,
    [dataSensorType]: type,
    [dataMetricName]: 'pwr'
  }

  return (
    <div className = "sensor-item">
      {
        configured ?
        <span className = "sensor-caption">{ getSensorCaption(type) }</span> :
        <Checkbox onChange = { onSelectChange } { ...attrs }><span className = "sensor-name">{ getSensorCaption(type) }</span></Checkbox>
      }
      <br/>
      {
        metricNames.length ?
        metricNames.map(name =>
          <span key = { name } className = "sensor-value">
            { equipmentMetricNames.get(name) || name }: { data[name] }
            &nbsp;
          </span>
        ) :
        <span className = "sensor-value">Нет данных</span>
      }
    </div>
  )
}

const ErrorCodes = [
  'Ok',
  'DeviceNotFound',
  'DeviceBusy',
  'DeviceNotSupported',
  'DriverNotFound',
  'NoConnection',
  'Timeout',
  'TransferFail',
  'InvalidArg',
  'FailToOpenChannel',
  'MaxChannelLimit'
]

function getErrorText(error) {
  let errorText

  if (error) errorText = `${ErrorCodes[error.code || -1] || 'Неизвестная ошибка'}`

  return errorText
}

function ViewSettingsSensors() {
  const sensors = useSelector(state => state.sensors);
  const connectivity = useSelector(state => state.connectivity);
  const { data, mode, error } = sensors
  const reachable = connectivity && connectivity.reachable
  const usb = connectivity && connectivity.usb
  const blocked = isTrainingBlocked(connectivity)

  const noData = isNoData(sensors)
  const configured = isConfigured(mode)

  const parent = useRef(null)
  const [ anyChecked, setAnyChecked ] = useState(false)

  const updateAnyChecked = useCallback(() => {
    const count = parent.current ? parent.current.querySelectorAll('input:checked').length : 0
    setAnyChecked(count > 0)
  }, [])

  useEffect(() => {
    fetchStatus()
    checkDaemonCompatibility()
  }, [])

  useEffect(() => {
    if (usb === true && mode === Modes.none) {
      startWork()
    }
  }, [usb, mode])

  const configure = useCallback(() => {
    const checked = [...parent.current.querySelectorAll('input:checked')]

    if (!checked.length) return

    const metrics = []

    checked.forEach(el => {
      metrics.push({
        id: +el.getAttribute(dataSensorId),
        type: +el.getAttribute(dataSensorType),
        name: el.getAttribute(dataMetricName)
      })
    })

    configureSensors(metrics)
  }, [parent])

  if (reachable === null) {
    return (
      <div>
        <ConnectingState message = { 'Проверка подключения к локальному сервису...' }/>
        <Button onClick={ fetchStatus }>Проверить</Button>
      </div>
    )
  } else if (reachable === false) {
    return (
      <div>
        <p>Локальный сервис недоступен. Убедитесь, что локальный сервис запущен.</p>
        <Button onClick={ fetchStatus }>Проверить</Button>
      </div>
    )
  } else if (reachable && !usb) {
    const handleSearch = async () => {
      await fetchStatus()
      if (store.getState().connectivity.usb === true) {
        fetchSensors()
      }
    }
    return (
      <div>
        { blocked ? <DaemonBlockBanner daemonApi = { connectivity.daemonApi }/> : undefined }
        <p>Локальный сервис запущен, но USB устройство не найдено. Подключите устройство и повторите поиск.</p>
        <Button onClick={ handleSearch }>Начать поиск датчиков</Button>
      </div>
    )
  } else if (mode === Modes.scanning && noData) {
    return (
      <div>
        { blocked ? <DaemonBlockBanner daemonApi = { connectivity.daemonApi }/> : undefined }
        <ConnectingState message = { 'Идёт поиск датчиков...' }/>
        <p>
          Датчики пока не найдены. Убедитесь, что они включены и находятся рядом,
          затем повторите поиск.
        </p>
        <Button onClick={ fetchSensors } icon={<SearchOutlined />}>Начать поиск заново</Button>
        <Button style={{ marginLeft: 8 }} onClick={ fetchStatus }>Проверить подключение</Button>
      </div>
    )
  } else if (mode === Modes.configurating) {
    return <ConnectingState message = { 'Настройка датчиков...' }/>
  } else {
    return (
      <div ref = { parent }>
        { blocked ? <DaemonBlockBanner daemonApi = { connectivity.daemonApi }/> : undefined }
        <ErrorMessage error = { mode === Modes.error ? error : undefined } getErrorText = { getErrorText } />
        {
          noData ?
          <NoDataState /> :
          <>
            {
              mode === Modes.scanning ? <p className = "sensors-page-caption">Новые датчики:</p> :
              mode === Modes.configured ? <p className = "sensors-page-caption">Сохраненные датчики:</p> :
              undefined
            }
            {
              data.metrics.map(item => <MetricsGroup key = { item.name } data = { item } configured = { configured } onSelectChange = { updateAnyChecked } />)
            }
            {
              data.equipment && data.equipment.map(item =>
                <EquipmentItem key = { `${item.type}-${item.id}` } equip = { item } configured = { configured } onSelectChange = { updateAnyChecked } />
              )
            }
            {
              !configured && mode !== Modes.configurating ? <>
                <br/>
                <Tooltip title = "Отмеченные датчики сохраняются локально в вашем браузере и будут автоматически подключаться при следующем запуске.">
                  <Button onClick={ configure } disabled = { !anyChecked }>Сохранить выбранные</Button>
                </Tooltip>
              </> : undefined
            }
            {
              configured ? <>
                <br/>
                <Tooltip title = "Отключит выбранные датчики и вернёт страницу к началу поиска.">
                  <Button danger = { true } onClick={ () => configureSensors([]) }>Забыть все датчики</Button>
                </Tooltip>
              </> : undefined
            }
          </>
        }
        {
          <><br/><br/><Button onClick={ fetchSensors } icon={<SearchOutlined />}>Начать поиск датчиков</Button></>
        }
      </div>
    )
  }
}

export default ViewSettingsSensors
